import type {
  CancelMedicationItemType,
  MedicationPadItemType
} from '@/components/PatientPageV2/components/PrescriptionPad';
import { getProductDisplayName } from '@/components/PatientPageV2/components/PrescriptionPad';
import { MedicationPrescriptionStatus } from '@montugroup/prescription-contracts';
import type { PrescriptionHistoryState, PrescriptionHistoryStatePreviousItem } from '../types';
import { getErrorMessageForMedicationItem } from './getErrorMessageForMedicationItem';

/**
 * Updates provided param `medicationPadItems` list with `updatedMedicationPadItem`
 * @param medicationPadItems - A list of medication pad items
 * @param updatedMedicationPadItem - Medication pad item to be updated within the list
 * @param ffPatPrescriptionDosageMinLengthValidation - flag reference for internal logic
 * @returns A new copy of the `medicationPadItems` list updated with `updatedMedicationPadItem` in `updatedItems`,
 * including the `updatedItem`.
 * If the product id has changed, a reference is returned for the `previousProductId`
 */
export const getUpdateMedicationPadItems = (
  medicationPadItems: MedicationPadItemType[],
  updatedMedicationPadItem: MedicationPadItemType,
  ffPatPrescriptionDosageMinLengthValidation: boolean,
  previousItems: PrescriptionHistoryStatePreviousItem[]
): {
  updatedItems: MedicationPadItemType[];
  updatedItem: MedicationPadItemType | undefined;
  previousProductId: number;
} => {
  let previousProductId: number = -1;
  let updatedItem: MedicationPadItemType | undefined = undefined;
  const updatedItems = medicationPadItems.map((item) => {
    if (item.medicationPadItemId === updatedMedicationPadItem.medicationPadItemId) {
      previousProductId = item.medicationPadItem?.productId ?? previousProductId;
      const errorMessage = getErrorMessageForMedicationItem(
        updatedMedicationPadItem.medicationPadItem,
        ffPatPrescriptionDosageMinLengthValidation
      );

      const previousItem = previousItems.find(
        (previousItem) => previousItem?.id === updatedMedicationPadItem.medicationPadItem?.productId
      );
      const reissueStatus = previousItem?.reissueStatus;
      updatedItem = {
        ...updatedMedicationPadItem,
        prescriptionStatus: previousItem?.prescriptionStatus ?? updatedMedicationPadItem?.prescriptionStatus,
        errorMessage,
        initialMedication:
          item.initialMedication ?? item.medicationPadItem ?? updatedMedicationPadItem.initialMedication,
        reissueStatus
      };
      return updatedItem;
    }
    return item;
  });

  return {
    updatedItems,
    updatedItem,
    previousProductId
  };
};

/**
 * Updates the reissue and cancel lists based on the new product selection.
 * @param updatedMedicationPadItem - The updated medication pad item object
 * @param previousItems - list of previously prescribed medications
 * @param itemsInReissue - list of medication items that are listed for reissue
 * @param itemsInCanceled - list of medication items that are listed for cancelation
 * @returns Updated lists of reissued and cancelled items, and the updated medication pad item if the product is being reissued
 */
export const handleNewProductSelection = (
  updatedMedicationPadItem: MedicationPadItemType,
  previousItems: PrescriptionHistoryState['previousItems'],
  itemsInReissue: MedicationPadItemType[],
  itemsInCanceled: CancelMedicationItemType[]
) => {
  const { medicationPadItem } = updatedMedicationPadItem;
  const updatedProductId = medicationPadItem?.productId;
  let updatedReissuedItems = [...itemsInReissue];
  let updatedCancelledItems = [...itemsInCanceled];
  let reissuedMedication;
  if (updatedProductId !== undefined) {
    // is it in previously prescribed items?
    const previouslyPrescribedNewSelection = previousItems.find(
      (item) =>
        item.id === updatedProductId &&
        ![MedicationPrescriptionStatus.Deleted, MedicationPrescriptionStatus.Error].includes(item.prescriptionStatus)
    );
    if (previouslyPrescribedNewSelection) {
      reissuedMedication = {
        ...updatedMedicationPadItem,
        reissueStatus: previouslyPrescribedNewSelection.reissueStatus,
        prescriptionStatus: previouslyPrescribedNewSelection.prescriptionStatus
      };
      updatedReissuedItems = [...updatedReissuedItems, reissuedMedication];
    }
    // if new item is in cancelled, remove it
    updatedCancelledItems = updatedCancelledItems.filter((item) => item.productId !== updatedProductId);
  }
  return {
    updatedReissuedItems,
    updatedCancelledItems,
    updatedMedicationPadItem: reissuedMedication
  };
};

/**
 * Updates the reissue and cancel lists based on the unselected product.
 * @param previousProductId - product Id of previous item selection
 * @param previousItems - list of previously prescribed medications
 * @param itemsInReissue - list of medication items that are listed for reissue
 * @param itemsInCanceled - list of medication items that are listed for cancelation
 * @returns Updated lists of reissued and cancelled items
 */
export const handleUnselectedProduct = (
  previousProductId: number,
  previousItems: PrescriptionHistoryState['previousItems'],
  itemsInReissue: MedicationPadItemType[],
  itemsInCanceled: CancelMedicationItemType[]
) => {
  let updatedReissuedItems = [...itemsInReissue];
  let updatedCancelledItems = [...itemsInCanceled];
  if (typeof previousProductId === 'number' && previousProductId !== -1) {
    const previouslyPrescribedActivePreviousSelection = previousItems.find(
      (item) => item.id === previousProductId && item.prescriptionStatus === MedicationPrescriptionStatus.Active
    );
    // handle reissue list: if previous item is currently in reissue, remove it
    updatedReissuedItems = updatedReissuedItems.filter(
      (item) => item.medicationPadItem?.productId !== previousProductId
    );
    // handle cancel list: only add to cancel list if product was previously prescribed and currently active
    if (previouslyPrescribedActivePreviousSelection) {
      const previousItemInReissuedList = itemsInReissue.find(
        (item) => item.medicationPadItem?.productId === previousProductId
      );

      updatedCancelledItems = [
        ...updatedCancelledItems,
        {
          productId: previousProductId,
          displayName: getProductDisplayName(
            previousItemInReissuedList?.medicationPadItem?.productName,
            previousItemInReissuedList?.medicationPadItem?.description,
            previousItemInReissuedList?.medicationPadItem?.supplierName ?? ''
          )
        }
      ];
    }
  }
  return {
    updatedReissuedItems,
    updatedCancelledItems
  };
};

/**
 * Updates the reissue and cancel lists when changing products.
 * @param updatedMedicationPadItem - The updated medication pad item object
 * @param previousItems - list of previously prescribed medications
 * @param itemsInReissue - list of medication items that are listed for reissue
 * @param itemsInCanceled - list of medication items that are listed for cancelation
 * @param previousProductId - product Id of previous item selection
 * @returns Updated lists of reissued and cancelled items, and the updated medication pad item if the product is being reissued
 */
export const updateReissueAndCancelWhenChangingProducts = (
  updatedMedicationPadItem: MedicationPadItemType,
  previousItems: PrescriptionHistoryState['previousItems'],
  itemsInReissue: MedicationPadItemType[],
  itemsInCanceled: CancelMedicationItemType[],
  previousProductId: number
): {
  updatedCancelledItems: CancelMedicationItemType[];
  updatedReissuedItems: MedicationPadItemType[];
  updatedMedicationPadItem?: MedicationPadItemType;
} => {
  const { medicationPadItem } = updatedMedicationPadItem;
  const updatedProductId = medicationPadItem?.productId;

  const updatedReissuedItems = [...itemsInReissue];
  const updatedCancelledItems = [...itemsInCanceled];
  let reissuedMedication;
  if (previousProductId !== updatedProductId) {
    // handle new product
    const newProductResult = handleNewProductSelection(
      updatedMedicationPadItem,
      previousItems,
      updatedReissuedItems,
      updatedCancelledItems
    );
    reissuedMedication = newProductResult.updatedMedicationPadItem;

    // handle previous product
    const unselectedProductResult = handleUnselectedProduct(
      previousProductId,
      previousItems,
      newProductResult.updatedReissuedItems,
      newProductResult.updatedCancelledItems
    );

    return {
      updatedCancelledItems: unselectedProductResult.updatedCancelledItems,
      updatedReissuedItems: unselectedProductResult.updatedReissuedItems,
      updatedMedicationPadItem: reissuedMedication
    };
  }

  return {
    updatedCancelledItems,
    updatedMedicationPadItem: reissuedMedication,
    updatedReissuedItems
  };
};
